import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import {
    BatchPushPushing,
    GET_BATCH_PUSH_RESULT,
    GetBatchPushResultResponse,
    GetBatchPushResultVariables,
    PUSH_STATUS,
} from './graphql/batchPush';
import { RESULT_POLL_INTERVAL_MS, RESULT_POLL_MAX_MS } from './components/BatchPushResultPanel';

/**
 * 盯住本仓进行中的推送（看板返回的 pushing）：每 2 秒轮询一次结果，推送结束（成功 / 失败）或超过最长轮询时间后
 * 回调 onFinished（页面据此刷新看板，「确认推送」恢复可用）。结束后不额外提示成功或失败。
 *
 * @param enabled 是否启用（结果页由 BatchPushResultPanel 自己轮询，此时传 false）
 */
export const usePushingWatcher = (
    warehouseId: number,
    pushing: BatchPushPushing | null | undefined,
    enabled: boolean,
    onFinished: () => void,
): void => {
    const pushRecordId = pushing?.pushRecordId != null ? Number(pushing.pushRecordId) : null;
    const active = enabled && pushRecordId != null && warehouseId > 0;
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const { data, stopPolling } = useQuery<GetBatchPushResultResponse, GetBatchPushResultVariables>(GET_BATCH_PUSH_RESULT, {
        variables: { warehouseId, pushRecordId: pushRecordId ?? 0 },
        skip: !active,
        fetchPolicy: 'network-only',
        pollInterval: RESULT_POLL_INTERVAL_MS,
    });
    const status = data?.result?.pushRecordId != null && Number(data.result.pushRecordId) === pushRecordId
        ? data.result.pushStatus
        : null;

    useEffect(() => {
        if (!active) return;
        if (status === PUSH_STATUS.SUCCESS || status === PUSH_STATUS.FAILED) {
            stopPolling();
            onFinishedRef.current();
        }
    }, [active, status, stopPolling]);

    // 兜底：推送中断时调度最长约 5 分钟后置为失败，这里 6 分钟后停止轮询并刷新看板
    useEffect(() => {
        if (!active) return undefined;
        const timer = setTimeout(() => {
            stopPolling();
            onFinishedRef.current();
        }, RESULT_POLL_MAX_MS);
        return () => clearTimeout(timer);
    }, [active, pushRecordId, stopPolling]);
};
